import type { ReviewFlag } from "../lib/api";
import { IconCheck, IconInfo } from "../ui/icons";

export default function ReviewFlagBanner({
  flags,
  passed,
}: {
  flags: ReviewFlag[] | null;
  passed: boolean | null;
}) {
  if (!flags || flags.length === 0) {
    if (passed) {
      return (
        <div className="inline-flex items-center gap-2 rounded-md border border-mint-200 bg-mint-100/50 text-ink-800 px-3 py-1.5 text-sm">
          <IconCheck size={14} className="text-mint-600" />
          Review passed with no notes
        </div>
      );
    }
    return null;
  }

  const flagged = flags.filter((f) => f.severity === "error");
  const suggestions = flags.filter((f) => f.severity === "warn");

  return (
    <div className="rounded-xl border border-ink-100 bg-white px-5 py-4 shadow-sm shadow-ink-900/5">
      <div className="flex items-start gap-3.5">
        <div className="shrink-0 mt-0.5 h-9 w-9 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center">
          <IconInfo size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-base font-semibold text-ink-800">
              Review notes
            </h3>
            {flagged.length > 0 && (
              <CountBadge
                count={flagged.length}
                label="Flagged"
                tone="red"
              />
            )}
            {suggestions.length > 0 && (
              <CountBadge
                count={suggestions.length}
                label={suggestions.length === 1 ? "Suggestion" : "Suggestions"}
                tone="amber"
              />
            )}
          </div>
          <p className="text-xs text-ink-500 mt-1">
            The review agent left{" "}
            {flags.length === 1 ? "one note" : `${flags.length} notes`} on this
            item. Flagged items usually need action; suggestions are optional.
          </p>
        </div>
      </div>

      <ul className="mt-4 pt-4 border-t border-ink-100 space-y-3.5">
        {flags.map((f, i) => (
          <FlagRow key={i} flag={f} />
        ))}
      </ul>
    </div>
  );
}

function CountBadge({
  count,
  label,
  tone,
}: {
  count: number;
  label: string;
  tone: "red" | "amber";
}) {
  const toneClass =
    tone === "red"
      ? "bg-red-50 text-red-800 border-red-100"
      : "bg-amber-50 text-amber-800 border-amber-100";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${toneClass}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          tone === "red" ? "bg-red-500" : "bg-amber-500"
        }`}
      />
      <span className="tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  );
}

function FlagRow({ flag }: { flag: ReviewFlag }) {
  const isFlagged = flag.severity === "error";
  return (
    <li className="flex items-start gap-3">
      <span
        className={`shrink-0 mt-[7px] h-2 w-2 rounded-full ${
          isFlagged ? "bg-red-500" : "bg-amber-500"
        }`}
        aria-label={isFlagged ? "Flagged" : "Suggestion"}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[11px] font-medium text-ink-500 uppercase tracking-wide">
            {flag.category.replace(/_/g, " ")}
          </span>
        </div>
        <p className="text-sm text-ink-800 leading-relaxed">{flag.message}</p>
        {flag.suggestion && (
          <p className="text-sm text-ink-600 mt-1 leading-relaxed">
            <span className="font-medium text-ink-500">Suggestion · </span>
            {flag.suggestion}
          </p>
        )}
      </div>
    </li>
  );
}
